import React, { useMemo } from 'react';
import { TrendingUp, TrendingDown, Mountain } from 'lucide-react';
import { ApexAreaChart } from '@/components/ui/premium-charts/ApexAreaChart';
import { TrackingPoint } from '@/services/tracking-service';
import {
  calculateCumulativeDistance,
  smoothAltitudeData,
  calculateElevationStats,
  formatAltitude,
  decimateData,
  ElevationData,
} from '@/utils/elevation-utils';

interface ElevationProfileProps {
  points: TrackingPoint[];
  currentDistance: number; // metros
  className?: string;
}

export const ElevationProfile: React.FC<ElevationProfileProps> = ({
  points,
  currentDistance,
  className = '',
}) => {
  const elevationData = useMemo(() => {
    // Filtrar pontos com altitude válida
    const validPoints = points.filter(
      (p) => p.altitude !== undefined && p.altitude !== null
    );

    if (validPoints.length < 3) {
      return null;
    }

    // Calcular distância acumulada
    const distances = calculateCumulativeDistance(validPoints);

    // Extrair altitudes e suavizar
    const rawAltitudes = validPoints.map((p) => p.altitude!);
    const smoothedAltitudes = smoothAltitudeData(rawAltitudes, 3);

    // Criar array de dados de elevação
    const data: ElevationData[] = validPoints.map((p, i) => ({
      distance: distances[i],
      altitude: smoothedAltitudes[i],
      timestamp: p.timestamp,
    }));

    // Decimação para performance
    const decimated = decimateData(data, 100);

    return decimated;
  }, [points]);

  const stats = useMemo(() => {
    if (!elevationData) return null;
    return calculateElevationStats(elevationData);
  }, [elevationData]);

  // Não renderizar se não houver dados
  if (!elevationData || !stats) {
    return null;
  }

  // Show stats even for flat routes (reduced threshold from 5m to 2m)
  const totalVariation = stats.maxAltitude - stats.minAltitude;
  const isFlatRoute = totalVariation < 2;

  // For very flat routes, still show stats but with a note
  if (isFlatRoute) {
    return (
      <div className={`bg-card rounded-lg border border-border p-4 ${className}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Mountain className="w-4 h-4" />
            <span className="text-sm">Percurso plano</span>
          </div>
          <span className="text-xs text-muted-foreground">
            Altitude: {formatAltitude(stats.currentAltitude)}
          </span>
        </div>
        {/* Show minimal stats even for flat routes */}
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-xs text-muted-foreground">Ganho</div>
            <div className="text-sm font-semibold">{formatAltitude(stats.totalGain)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Perda</div>
            <div className="text-sm font-semibold">{formatAltitude(stats.totalLoss)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Atual</div>
            <div className="text-sm font-semibold">{formatAltitude(stats.currentAltitude)}</div>
          </div>
        </div>
      </div>
    );
  }

  // Preparar dados para o gráfico
  const chartSeries = [
    {
      name: 'Altitude',
      data: elevationData.map((d) => Math.round(d.altitude)),
    },
  ];

  const chartCategories = elevationData.map((d) => d.distance.toFixed(2));

  const currentDistanceKm = currentDistance / 1000;

  return (
    <div className={`bg-card rounded-lg border border-border p-4 space-y-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Mountain className="w-4 h-4" />
          Perfil de Elevação
        </h3>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-green-500" />
          <div>
            <div className="text-xs text-muted-foreground">Ganho</div>
            <div className="text-sm font-semibold text-foreground">
              {formatAltitude(stats.totalGain)}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <TrendingDown className="w-4 h-4 text-red-500" />
          <div>
            <div className="text-xs text-muted-foreground">Perda</div>
            <div className="text-sm font-semibold text-foreground">
              {formatAltitude(stats.totalLoss)}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Mountain className="w-4 h-4 text-primary" />
          <div>
            <div className="text-xs text-muted-foreground">Atual</div>
            <div className="text-sm font-semibold text-foreground">
              {formatAltitude(stats.currentAltitude)}
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="h-[180px]">
        <ApexAreaChart
          height={180}
          series={chartSeries}
          categories={chartCategories}
          formatter={(val) => `${Math.round(val)}m`}
        />
      </div>
    </div>
  );
};
